import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Alert } from "../components/Alert";

interface Message {
    text: string;
    type: 'error' | 'warning' | 'success';
}

export const EmailActivation: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const apiUrl = import.meta.env.VITE_API_URL as string;
    const [message, setMessage] = useState<Message>({ text: '', type: 'success' });
    const [isActivating, setIsActivating] = useState<boolean>(true);
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
    const navigate = useNavigate();
    const hasActivated = useRef<boolean>(false);

    useEffect(() => {
        const activateEmail = async () => {
            // Prevent duplicate calls
            if (hasActivated.current || !isActivating) return;

            hasActivated.current = true;

            // Ensure minimum display time for "Activating Email..." message
            const startTime = Date.now();
            const minDisplayTime = 1000;

            try {
                const response = await fetch(`${apiUrl}/auth/activate/${token}`, {
                    method: 'GET',
                });

                const data = await response.json();

                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max(0, minDisplayTime - elapsed);

                setTimeout(() => {
                    if (response.ok) {
                        setMessage({ text: data.message, type: 'success' });
                        setIsActivating(false);
                        setIsRedirecting(true);
                    } else {
                        setMessage({ text: data.error, type: 'error' });
                        setIsActivating(false);
                    }
                }, remainingTime);

            } catch {
                const elapsed = Date.now() - startTime;
                const remainingTime = Math.max(0, minDisplayTime - elapsed);

                setTimeout(() => {
                    setMessage({ text: 'Network error. Please check your connection.', type: 'error' });
                    setIsActivating(false);
                }, remainingTime);
            }
        };

        activateEmail();
    }, [apiUrl, token, isActivating]);

    useEffect(() => {
        let redirectTimer: number | undefined;
        if (isRedirecting) {
            redirectTimer = window.setTimeout(() => {
                navigate('/login', { replace: true });
            }, 2000);
        }
        return () => {
            if (redirectTimer) {
                clearTimeout(redirectTimer);
            }
        };
    }, [isRedirecting, navigate]);

    if (isActivating) {
        return (
            <div className="act-layout">
                <div className="lcont">
                    <h1 className="text-2xl font-bold">Activating Email...</h1>
                    <div className="flex justify-center items-center mt-6">
                        <span className="loading loading-spinner loading-lg text-primary"></span>
                    </div>
                    <p className="text-sm">Please wait while we activate your account.</p>
                </div>
            </div>
        );
    }

    if (message.type === 'error') {
        return (
            <div className="act-layout">
                <div className="lcont">
                    <Alert type={message.type} message={message.text} />
                    <Link to="/resend-activation" className="btn btn-primary mt-4">Request New Activation Link</Link>
                    <p className="text-sm mt-4">
                        Or <Link to="/login" className="text-primary hover:underline">Login</Link>
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="act-layout">
            <div className="lcont">
                <Alert type={message.type} message={message.text} />
                <div className="flex justify-center items-center mt-6">
                    <span className="loading loading-spinner loading-lg text-primary"></span>
                </div>
                <p className="mt-2 text-sm">Redirecting to login...</p>
                <p className="mt-4">
                    If you are not redirected, <Link to="/login" className="text-primary hover:underline">click here to login</Link>.
                </p>
            </div>
        </div>
    );
};
